import useSWR from "swr";
import { supabase } from "@/integrations/supabase/client";
import { menRankings, womenRankings, rankingsMeta, type Surfer } from "@/data/rankings";

type RankingsData = { men: Surfer[]; women: Surfer[]; meta: typeof rankingsMeta };

const fallback: RankingsData = { men: menRankings, women: womenRankings, meta: rankingsMeta };

// Best-effort country → flag emoji. The synced data only has a country name
// (scraped from an image alt attribute), not an ISO code, so this is a
// lookup rather than a proper conversion — extend as needed.
const FLAGS: Record<string, string> = {
  "brazil": "🇧🇷", "australia": "🇦🇺", "united states": "🇺🇸", "usa": "🇺🇸",
  "hawaii": "🇺🇸", "hawaii, usa": "🇺🇸", "france": "🇫🇷", "portugal": "🇵🇹",
  "south africa": "🇿🇦", "japan": "🇯🇵", "italy": "🇮🇹", "indonesia": "🇮🇩",
  "new zealand": "🇳🇿", "spain": "🇪🇸", "el salvador": "🇸🇻", "peru": "🇵🇪",
  "united kingdom": "🇬🇧", "morocco": "🇲🇦", "fiji": "🇫🇯",
};
const flagFor = (country: string | null) => (country ? FLAGS[country.toLowerCase()] ?? "🏳️" : "🏳️");

// "2026-09-11" + "2026-09-20" -> "Sep 11 – 20, 2026" (or "Oct 22 – Nov 1, 2026" across months),
// matching the display format the static fallback data already used.
function formatWindow(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return "";
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const month = (d: Date) => d.toLocaleDateString(undefined, { month: "short" });
  const day = (d: Date) => d.getUTCDate();
  const year = end.getUTCFullYear();
  const startPart = month(start) === month(end) ? `${month(start)} ${day(start)}` : `${month(start)} ${day(start)}`;
  const endPart = month(start) === month(end) ? `${day(end)}` : `${month(end)} ${day(end)}`;
  return `${startPart} – ${endPart}, ${year}`;
}

type EventRow = {
  name: string;
  location: string | null;
  country: string | null;
  start_date: string | null;
  end_date: string | null;
};

async function fetchMetaFromEvents(fallbackMeta: typeof rankingsMeta): Promise<typeof rankingsMeta> {
  const { data, error } = await supabase
    .from("wsl_events")
    .select("name, location, country, start_date, end_date")
    .order("start_date", { ascending: true });

  if (error || !data || data.length === 0) return fallbackMeta;

  const today = new Date().toISOString().slice(0, 10);
  const rows = data as EventRow[];
  const completed = rows.filter((e) => e.end_date && e.end_date < today);
  const upcoming = rows.filter((e) => e.end_date && e.end_date >= today);
  const last = completed.at(-1);
  const next = upcoming[0] ?? completed.at(-1);
  if (!last && !next) return fallbackMeta;

  const locOf = (e?: EventRow) => (e ? [e.location, e.country].filter(Boolean).join(", ") : "");

  return {
    lastUpdated: fallbackMeta.lastUpdated,
    afterEvent: completed.length,
    lastEventName: last?.name ?? fallbackMeta.lastEventName,
    lastEventLocation: locOf(last) || fallbackMeta.lastEventLocation,
    nextEventName: next?.name ?? fallbackMeta.nextEventName,
    nextEventWindow: next ? formatWindow(next.start_date, next.end_date) : fallbackMeta.nextEventWindow,
    nextEventLocation: locOf(next) || fallbackMeta.nextEventLocation,
  };
}

async function fetchFromSupabase(): Promise<RankingsData> {
  const { data, error } = await supabase
    .from("wsl_rankings")
    .select("*")
    .order("rank", { ascending: true });

  if (error) throw error;
  if (!data || data.length === 0) return fallback; // table not synced yet — never show an empty screen

  const toSurfer = (row: (typeof data)[number]): Surfer => ({
    rank: row.rank,
    name: row.name,
    country: row.country ?? "",
    flag: flagFor(row.country),
    points: row.points ?? 0,
    events: row.events ?? 0,
    bestResult: row.best_result || "—",
    results: Array.isArray(row.results) ? row.results : [],
  });

  const men = data.filter((r) => r.tour === "men").map(toSurfer);
  const women = data.filter((r) => r.tour === "women").map(toSurfer);

  const { data: log } = await supabase
    .from("wsl_sync_log")
    .select("ran_at")
    .order("ran_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  const lastUpdated = log?.ran_at?.slice(0, 10) ?? fallback.meta.lastUpdated;
  const meta = await fetchMetaFromEvents({ ...fallback.meta, lastUpdated });

  return {
    men: men.length > 0 ? men : fallback.men,
    women: women.length > 0 ? women : fallback.women,
    meta,
  };
}

export function useRankings() {
  const { data, error, isLoading, isValidating, mutate } = useSWR<RankingsData>(
    "wsl-rankings",
    fetchFromSupabase,
    {
      refreshInterval: 60 * 60 * 1000, // re-check every hour
      fallbackData: fallback,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 30 * 60 * 1000,
    }
  );

  return {
    men: data?.men ?? fallback.men,
    women: data?.women ?? fallback.women,
    meta: data?.meta ?? fallback.meta,
    isLoading,
    isValidating,
    isError: !!error,
    refresh: mutate,
  };
}
